function getSegmentResourceId(seg) {
  const ridRaw = seg?.resourceId?._id || seg?.resourceId || null;
  return ridRaw ? String(ridRaw) : null;
}

function getSegmentResourceName(seg, nameById) {
  if (seg?.resourceId && typeof seg.resourceId === 'object' && seg.resourceId?.name) {
    return String(seg.resourceId.name);
  }
  const rid = getSegmentResourceId(seg);
  if (!rid) return null;
  const fromMap = nameById instanceof Map ? nameById.get(rid) : null;
  return fromMap || rid.substring(0, 6);
}

function collapseConsecutive(items) {
  if (!Array.isArray(items) || items.length === 0) return [];
  const out = [];
  for (const it of items) {
    if (!it) continue;
    const last = out.length > 0 ? out[out.length - 1] : null;
    if (last && String(last) === String(it)) continue;
    out.push(it);
  }
  return out;
}

function formatGroupRanges(indices) {
  const sorted = Array.from(new Set(indices.map(n => Number(n)).filter(n => Number.isFinite(n)))).sort(
    (a, b) => a - b
  );
  if (sorted.length === 0) return '';
  if (sorted.length === 1) return `${sorted[0]}`;

  const ranges = [];
  let start = sorted[0];
  let prev = sorted[0];
  for (let i = 1; i < sorted.length; i += 1) {
    const v = sorted[i];
    if (v === prev + 1) {
      prev = v;
      continue;
    }
    ranges.push(start === prev ? `${start}` : `${start}–${prev}`);
    start = v;
    prev = v;
  }
  ranges.push(start === prev ? `${start}` : `${start}–${prev}`);
  return ranges.join(',');
}

/**
 * Returns a compact, per-group ordered-resources display string.
 *
 * Examples:
 * - Single group: "R1 → R2"
 * - Multiple groups, same path: "G1–G4: R1"
 * - Multiple groups, different paths: "G1: R1 → R2 | G2–G3: R1"
 */
export function formatOrderedResourcesByGroup(booking, nameById, { maxParts = 2 } = {}) {
  try {
    const segs = Array.isArray(booking?.orderedResourceIds) ? booking.orderedResourceIds : [];
    if (segs.length === 0) return '';

    const byGroup = new Map();
    segs.forEach((seg, idx) => {
      const rid = getSegmentResourceId(seg);
      if (!rid) return;
      const groupRaw = Number(seg?.groupIndex);
      const groupIndex = Number.isFinite(groupRaw) && groupRaw > 0 ? groupRaw : 1;

      const orderPosRaw = Number(seg?.orderPosition);
      const orderPosition = Number.isFinite(orderPosRaw) && orderPosRaw > 0 ? orderPosRaw : idx + 1;

      const offRaw = Number(seg?.startOffsetMinutes);
      const startOffsetMinutes = Number.isFinite(offRaw) ? offRaw : null;

      if (!byGroup.has(groupIndex)) byGroup.set(groupIndex, []);
      byGroup.get(groupIndex).push({
        seg,
        rid,
        orderPosition,
        startOffsetMinutes,
      });
    });

    const groupEntries = Array.from(byGroup.entries()).map(([groupIndex, entries]) => {
      const list = Array.isArray(entries) ? entries.slice() : [];
      const hasAllOffsets = list.every(e => Number.isFinite(e.startOffsetMinutes));
      list.sort((a, b) => {
        if (hasAllOffsets) return a.startOffsetMinutes - b.startOffsetMinutes;
        return a.orderPosition - b.orderPosition;
      });

      const names = collapseConsecutive(list.map(e => getSegmentResourceName(e.seg, nameById)).filter(Boolean));
      const path = names.join(' → ').trim();

      const sortKey = (() => {
        const offs = list
          .map(e => (Number.isFinite(e.startOffsetMinutes) ? Number(e.startOffsetMinutes) : null))
          .filter(v => v != null);
        if (offs.length > 0) return Math.min(...offs);
        return Number(groupIndex);
      })();

      return { groupIndex: Number(groupIndex), sortKey, path };
    });

    groupEntries.sort((a, b) => a.sortKey - b.sortKey);

    const groupCount = groupEntries.length;
    if (groupCount === 0) return '';
    if (groupCount === 1) return groupEntries[0].path || '';

    const pathToGroups = new Map();
    groupEntries.forEach(g => {
      if (!g.path) return;
      if (!pathToGroups.has(g.path)) pathToGroups.set(g.path, []);
      pathToGroups.get(g.path).push(g.groupIndex);
    });

    const parts = Array.from(pathToGroups.entries()).map(([path, groupIndices]) => {
      const range = formatGroupRanges(groupIndices);
      return `G${range}: ${path}`;
    });

    if (parts.length <= maxParts) return parts.join(' | ');
    const shown = parts.slice(0, maxParts);
    const remaining = parts.length - shown.length;
    return `${shown.join(' | ')} | +${remaining}`;
  } catch (_) {
    return '';
  }
}

